import { apiGet, apiPost } from "@softmax/observatory-client/client";
import {
  DEFAULT_BACKEND_FETCH_TIMEOUT_MS,
  type ApiTransport,
} from "@softmax/observatory-client/transport";
import { withRequestDeadline } from "@softmax/observatory-client/request-signal";

import { watchableLeagues } from "@softmax/web-features/leagues";

import { navigationLeagues } from "@/data/catalog";
import { publicWatchApi } from "@/data/api";
import { type WatchTheaterSeed } from "@/lib/leagues/watch-types";
import { loadWatchLeagueState } from "@/lib/leagues/watch";
import { slimLeagueForTheater } from "@/lib/leagues/watch-seed-slim";
import { listedPublic } from "@/lib/leagues/browse-hidden";
import {
  type WatchSeedAddress,
  storedWatchLeague,
  watchLeagueFor,
} from "./requests/watch-address.data";
import { resolveSelectedWatchLeague } from "./requests/selected-league.data";

export async function watchSeed({
  signal,
  address,
  withState = true,
  withNavigation = true,
}: {
  signal?: AbortSignal | undefined;
  address: WatchSeedAddress;
  withState?: boolean;
  withNavigation?: boolean;
}): Promise<WatchTheaterSeed> {
  const generatedAt = Date.now();
  const { stored = storedWatchLeague() } = address;
  let leagues = address.corpus;
  let viewer = address.viewer ?? null;
  const hideBrowseHidden = address.corpus === undefined;

  if (leagues === undefined) {
    const selectedId = address.leagueId ?? stored;
    const path = selectedId
      ? `/v2/leagues/${encodeURIComponent(selectedId)}`
      : null;
    const resolved = await resolveSelectedWatchLeague({
      signal,
      path,
    });

    const selection = listedPublic(
      resolved.selected ? watchableLeagues([resolved.selected]) : [],
    );
    const navigation =
      !withNavigation && selection.length > 0
        ? []
        : await navigationLeagues({ signal });
    leagues = listedPublic([
      ...selection,
      ...navigation.filter((league) => league.id !== selection[0]?.id),
    ]);
    viewer = selection.length > 0 ? resolved.viewer : null;
  }

  if (!leagues)
    return {
      generatedAt,
      leagues: null,
      state: null,
      stored,
      viewer,
      hideBrowseHidden,
    };

  const seed: WatchTheaterSeed = {
    generatedAt,
    leagues: leagues.map(slimLeagueForTheater),
    state: null,
    stored,
    viewer,
    hideBrowseHidden,
  };

  if (!withState) return seed;

  const league = await watchLeagueFor({
    signal,
    address: { ...address, corpus: leagues },
  });

  if (!league) return seed;

  const requestSignal = withRequestDeadline(
    signal,
    DEFAULT_BACKEND_FETCH_TIMEOUT_MS,
  );
  const api: ApiTransport = viewer
    ? {
        get: <T>(path: string) =>
          apiGet<T>(path, { elevatedPrivileges: false, signal: requestSignal }),
        post: <T>(path: string, body?: unknown) =>
          apiPost<T>(path, body, {
            elevatedPrivileges: false,
            signal: requestSignal,
          }),
      }
    : publicWatchApi({ signal: requestSignal });
  return {
    ...seed,
    state: await loadWatchLeagueState({ api, league, wave: "feature" }),
  };
}
